import { useCallback, useEffect, useRef, useState } from 'react';

// Tracks an element's rendered height (offsetHeight, so margins on its children
// and its own border/padding are included). Used to give a layout a real height
// budget when the content that fills it is text of unknown length - the number
// of lines a merchant-authored string wraps into can't be derived from the
// viewport alone, it has to be measured.
//
// Re-measures whenever the element reflows (ResizeObserver) and on viewport
// resize / orientation change. Where ResizeObserver is unavailable (jsdom in
// tests, very old WebViews) the height stays 0, which callers should treat as
// "unknown" and fall back to their unmeasured sizing.
export default function useMeasuredHeight<
  T extends HTMLElement,
>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);

  const measure = useCallback(() => {
    const node = ref.current;
    if (node) {
      setHeight(node.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === 'undefined') {
      return undefined;
    }

    measure();

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      observer?.disconnect();
    };
  }, [measure]);

  return [ref, height];
}
